import { Grid, MenuItem, menuItemClasses, SelectChangeEvent } from '@mui/material';
import { useFormikContext } from 'formik';
import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import DownIcon from '../../../../../../../assets/icons/Icon_ArrowDown.svg?react';
import { ResourceType, useQueryGetGetResourcesByType } from '../../../../../../../backend';
import { errorToastConfig } from '../../../../../../../utils/utils';
import { ResourcesPageFormValues } from '../../../../../../pages/ResourcesPage';
import { StyledMuiTextField, StyledSelect } from '../ResourceFieldsLineProperties.style';
import { isArrivalHallProperties, isDepartureHallProperties } from './ResourceFieldsLine.utils';
import { useReplaceNumberAt, useReplaceSelectAt } from './useReplaceValueAt';

export const ResourceFieldsLineHallProperties = memo(function ResourceFieldsLineHallProperties({
    index,
    arrayReplace,
}: {
    index: number;
    arrayReplace: (index: number, value: any) => void;
}) {
    const { values, initialValues, errors } = useFormikContext<ResourcesPageFormValues>();

    const { data: terminals } = useQueryGetGetResourcesByType(
        {
            type: ResourceType.TERMINAL,
            includeNonActive: true,
        },
        {
            onError: () => {
                toast(<FormattedMessage id={'resourcesForm.error.default'} />, errorToastConfig);
            },
        },
    );

    const handleNumberChange = useReplaceNumberAt(index, arrayReplace);

    const handleSelectChange = useReplaceSelectAt(index, arrayReplace);

    const content = useMemo(() => values.resources[index].content, [index, values.resources]);
    const initialContent = useMemo(
        () => (values.resources[index].id ? initialValues.resources[index].content : null),
        [index, initialValues.resources, values.resources],
    );

    return isArrivalHallProperties(content) || isDepartureHallProperties(content) ? (
        <>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={content.surface}
                    onChange={e => handleNumberChange('surface', e)}
                    modified={
                        values.resources[index].id &&
                        (isArrivalHallProperties(initialContent) || isDepartureHallProperties(initialContent))
                            ? content.surface !== initialContent.surface
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0, step: 0.1 },
                    }}
                    error={!!errors[`resources[${index}]['surface']`]}
                    helperText={errors[`resources[${index}]['surface']`]}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={content.passengersBySquareMeter}
                    onChange={e => handleNumberChange('passengersBySquareMeter', e)}
                    modified={
                        values.resources[index].id &&
                        (isArrivalHallProperties(initialContent) || isDepartureHallProperties(initialContent))
                            ? content.passengersBySquareMeter !== initialContent.passengersBySquareMeter
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0, step: 0.1 },
                    }}
                    error={!!errors[`resources[${index}]['passengersBySquareMeter']`]}
                    helperText={errors[`resources[${index}]['passengersBySquareMeter']`]}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={isArrivalHallProperties(content) ? content.offsetSTA : content.offsetSTD}
                    onChange={e => handleNumberChange(isArrivalHallProperties(content) ? 'offsetSTA' : 'offsetSTD', e)}
                    modified={
                        values.resources[index].id
                            ? isArrivalHallProperties(content) && isArrivalHallProperties(initialContent)
                                ? content.offsetSTA !== initialContent.offsetSTA
                                : isDepartureHallProperties(content) && isDepartureHallProperties(initialContent)
                                ? content.offsetSTD !== initialContent.offsetSTD
                                : undefined
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0 },
                    }}
                    error={
                        isArrivalHallProperties(content)
                            ? !!errors[`resources[${index}]['offsetSTA']`]
                            : !!errors[`resources[${index}]['offsetSTD']`]
                    }
                    helperText={
                        isArrivalHallProperties(content)
                            ? errors[`resources[${index}]['offsetSTA']`]
                            : errors[`resources[${index}]['offsetSTD']`]
                    }
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledSelect
                    value={terminals?.length === 0 ? '' : content.terminal}
                    onChange={(e: SelectChangeEvent<string>) => handleSelectChange('terminal', e)}
                    IconComponent={DownIcon}
                    modified={
                        values.resources[index].id &&
                        (isArrivalHallProperties(initialContent) || isDepartureHallProperties(initialContent))
                            ? content.terminal !== initialContent.terminal
                            : undefined
                    }
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                [`& .${menuItemClasses.root}`]: {
                                    color: theme => theme.palette.primary.contrastText,
                                },
                            },
                        },
                    }}
                >
                    <MenuItem key={'empty'} value={''}>
                        {' '}
                    </MenuItem>
                    {terminals
                        ?.map(r => r.name)
                        .map(name => (
                            <MenuItem key={name} value={name}>
                                {name}
                            </MenuItem>
                        ))}
                </StyledSelect>
            </Grid>
        </>
    ) : null;
});
